/**
 * Leaving the webhook attach wizard to connect an account, and coming back.
 *
 * The wizard is parked in this tab's sessionStorage under the person's id, and
 * the Integrations page is given a `returnTo` (only ever the webhooks list) and
 * the provider to `connect`. The saved wizard holds workflow inputs, so it is
 * bound to its owner, expires, is removed on first read, and is cleared on
 * sign-out (`clearClientAuthStateOnLogout`).
 */

export const WEBHOOK_ATTACH_RETURN_KEY = "webhook-attach-return";
export const WEBHOOK_ATTACH_RETURN_MAX_AGE_MS = 30 * 60 * 1000;

type SavedWizard = { provider?: string };

export function saveWebhookAttachReturn(
  wizard: SavedWizard,
  userId: string,
): void {
  if (!userId) return;
  try {
    sessionStorage.setItem(
      WEBHOOK_ATTACH_RETURN_KEY,
      JSON.stringify({ savedAt: Date.now(), userId, wizard }),
    );
  } catch {
    // Private mode can refuse sessionStorage. The return URL still brings
    // the user back to the webhooks list.
  }
}

export function takeWebhookAttachReturn<T extends SavedWizard>(
  userId: string,
): T | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(WEBHOOK_ATTACH_RETURN_KEY);
    sessionStorage.removeItem(WEBHOOK_ATTACH_RETURN_KEY);
  } catch {
    return null;
  }
  if (!raw || !userId) return null;
  try {
    const parsed = JSON.parse(raw) as {
      savedAt?: number;
      userId?: string;
      wizard?: T;
    };
    // A missing or different owner means another person signed in in this tab.
    // The entry is already removed above, so it cannot be restored later.
    if (parsed.userId !== userId) return null;
    if (!parsed.wizard?.provider || typeof parsed.savedAt !== "number") {
      return null;
    }
    if (Date.now() - parsed.savedAt > WEBHOOK_ATTACH_RETURN_MAX_AGE_MS) {
      return null;
    }
    return parsed.wizard;
  } catch {
    return null;
  }
}

export function clearWebhookAttachReturn(): void {
  try {
    sessionStorage.removeItem(WEBHOOK_ATTACH_RETURN_KEY);
  } catch {
    // Private mode can refuse sessionStorage.
  }
}

/**
 * Where the Integrations page may send someone back to. Only the webhooks
 * list is allowed, so a crafted returnTo cannot leave the app.
 */
export function webhooksReturnPath(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return null;
  try {
    const url = new URL(trimmed, "http://localhost");
    if (url.origin !== "http://localhost") return null;
    if (url.pathname !== "/u/webhooks") return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

/** Catalog slug carried on the URL so that provider's sheet can open. */
export function integrationConnectSlug(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!/^[a-z0-9_]+$/.test(trimmed)) return null;
  return trimmed;
}

/**
 * Whether a refresh of the connected set means the provider the wizard asked
 * for has just been connected. The Integrations page refreshes that set after
 * every connect, delete, account switch and closed OAuth popup; only a new
 * connection of the requested provider is a reason to go back to the wizard.
 * The set holds `integration:action` keys.
 */
export function connectedJustNow(
  before: ReadonlySet<string>,
  after: ReadonlySet<string>,
  slug: string,
): boolean {
  const has = (set: ReadonlySet<string>) => {
    for (const key of set) if (key.startsWith(`${slug}:`)) return true;
    return false;
  };
  return has(after) && !has(before);
}
